/**
 * Field suggestions from a label the admin typed as `{{label}}`. Shared by the
 * .docx importer and the editor so both create fields the same way.
 *
 * The label is kept exactly as typed: it is what the executive is asked
 * ("NAME", "father name"). A choice can be written inside the braces:
 *   {{Relation: S/o, D/o, W/o}}  -> asks "Relation" with a list S/o · D/o · W/o
 *   {{S/o, D/o, W/o}}            -> asks "S/o, D/o, W/o" with that list
 * Options are separated by commas, "|" or " / " (with spaces, so "S/o" stays whole).
 *
 * Tier 3 types are guessed first and generously: calling an Aadhaar field
 * "text" would sync it to the server; the reverse mistake is harmless.
 */
import { FIELD_ID_PATTERN, type FieldDef, type FieldType } from '@saledeed/shared';
import { looksSensitive } from './validate-template';

const DATE_LABEL = /\bdate\b|\bdated\b|\bdt\b|\bdob\b|తేదీ/i;
const AMOUNT_LABEL = /amount|consideration|price|\bvalue\b|\brs\b|rupees|₹|మొత్తం/i;
/** S/o, D/o, W/o, C/o, H/o: a list of only these is a choice even without "Label:". */
const RELATION = /^[sdwch]\s?\/\s?o\.?$/i;

export function slugForLabel(label: string): string {
  return label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
}

export function guessFieldType(label: string): FieldType {
  return looksSensitive(label) ?? (DATE_LABEL.test(label) ? 'date' : AMOUNT_LABEL.test(label) ? 'currency' : 'text');
}

export interface FieldSpec {
  /** What the executive is asked. */
  label: string;
  /** Present when the braces hold a choice: the options, in the admin's order. */
  options?: string[];
}

function splitOptions(text: string): string[] | null {
  const parts = (text.includes('|') ? text.split('|') : text.includes(',') ? text.split(',') : text.split(/\s\/\s/))
    .map(x => x.trim())
    .filter(Boolean);
  const unique = [...new Set(parts)];
  if (unique.length < 2 || unique.length > 20 || unique.some(o => o.length > 80)) return null;
  return unique;
}

/** Reads what the admin wrote inside {{ }}: a label, and options when it is a choice. */
export function parseFieldSpec(raw: string): FieldSpec {
  const text = raw.replace(/\s+/g, ' ').trim();
  const colon = text.indexOf(':');
  if (colon > 0) {
    const label = text.slice(0, colon).trim();
    const options = splitOptions(text.slice(colon + 1));
    if (label && options) return { label: label.slice(0, 120), options };
  }
  // "S/o, D/o, W/o" or "Representative S/o, D/o, W/o": the relations are the options.
  const parts = splitOptions(text);
  if (parts) {
    const firstWord = parts[0]!.split(' ').pop()!;
    const options = [firstWord, ...parts.slice(1)];
    if (options.every(o => RELATION.test(o))) return { label: text.slice(0, 120), options };
  }
  return { label: text.slice(0, 120) };
}

/** A new field for `label`, with an id unique among `taken`. Does not register it. */
export function suggestField(rawLabel: string, scope: FieldDef['scope'], taken: ReadonlySet<string>): FieldDef {
  const { label, options } = parseFieldSpec(rawLabel);
  let base = slugForLabel(label);
  if (!FIELD_ID_PATTERN.test(base)) base = 'field';
  let id = base;
  for (let n = 1; taken.has(id) || !FIELD_ID_PATTERN.test(id) || id === 'field'; n++) id = `${base.slice(0, 58)}_${n}`;
  return options
    ? { id, label, type: 'enum', scope, required: false, options }
    : { id, label, type: guessFieldType(label), scope, required: false };
}

/** Case- and space-insensitive label lookup; `{{Relation: S/o, D/o}}` finds "Relation". */
export function findFieldByLabel(fields: readonly FieldDef[], label: string): FieldDef | undefined {
  const key = parseFieldSpec(label).label.toLowerCase();
  return fields.find(f => f.label.toLowerCase() === key);
}

/**
 * The field with the options the admin just wrote, when he wrote a choice for
 * an existing text or choice field; otherwise null (nothing to change).
 */
export function withSpecOptions(field: FieldDef, raw: string): FieldDef | null {
  const { options } = parseFieldSpec(raw);
  if (!options || (field.type !== 'text' && field.type !== 'enum')) return null;
  if (field.type === 'enum' && field.options?.join('\u0000') === options.join('\u0000')) return null;
  const { maxLength: _maxLength, ...rest } = field;
  return { ...rest, type: 'enum', options };
}
